<script lang="ts">
  import { onMount, tick } from 'svelte';
  import type { Channel } from '@multiview/shared';
  import { api, type ChannelGroup } from '../lib/api/client.ts';

  interface Props {
    /** What the pick is for, e.g. the match; shown as the heading. */
    title: string;
    onPick: (channel: Channel) => void;
    onCancel: () => void;
    /** Channel ids already on screen, so they can be marked. */
    usedChannelIds: number[];
  }
  const { title, onPick, onCancel, usedChannelIds }: Props = $props();

  let channels = $state<Channel[]>([]);
  let groups = $state<ChannelGroup[]>([]);
  let total = $state(0);
  let query = $state('');
  let group = $state('');
  let favoritesOnly = $state(false);
  let loading = $state(false);
  let error = $state<string | null>(null);
  let nowPlaying = $state<Record<number, { title: string; start: number; stop: number }>>({});
  let searchInput = $state<HTMLInputElement | null>(null);

  async function load() {
    loading = true;
    error = null;
    try {
      const page = await api.listChannels({
        q: query.trim() || undefined,
        group: group || undefined,
        favoritesOnly,
        limit: 80,
      });
      channels = page.channels;
      total = page.total;
      // Missing EPG data is normal and must not break the list.
      void api.epgNow(page.channels.map((c) => c.id))
        .then((r) => { nowPlaying = r.now; })
        .catch(() => { nowPlaying = {}; });
    } catch (err) {
      error = err instanceof Error ? err.message : String(err);
    } finally {
      loading = false;
    }
  }

  onMount(() => {
    void api.channelGroups().then((g) => { groups = g; }).catch(() => {});
    // Focus lands in the picker, not back on the button that opened it.
    void tick().then(() => searchInput?.focus());
  });

  // Debounced so typing a channel name does not issue a query per keystroke.
  let searchTimer: number | null = null;
  $effect(() => {
    const _deps = [query, group, favoritesOnly];
    void _deps;
    if (searchTimer !== null) clearTimeout(searchTimer);
    searchTimer = window.setTimeout(() => void load(), 220);
    return () => { if (searchTimer !== null) clearTimeout(searchTimer); };
  });
</script>

<div class="picker">
  <div class="head">
    <h4>{title} <span class="faint">{total}</span></h4>
    <button data-focusable onclick={onCancel}>Abbrechen</button>
  </div>

  <div class="filters">
    <input bind:this={searchInput} bind:value={query} data-focusable placeholder="Sender suchen …" aria-label="Sender suchen" />
    <select bind:value={group} data-focusable aria-label="Gruppe">
      <option value="">Alle Gruppen</option>
      {#each groups as g (g.name)}
        <option value={g.name}>{g.name} ({g.count})</option>
      {/each}
    </select>
    <label class="check">
      <input type="checkbox" bind:checked={favoritesOnly} data-focusable />
      <span>Nur Favoriten</span>
    </label>
  </div>

  {#if error}<p class="error">{error}</p>{/if}

  <ul class="list scroll" class:dim={loading}>
    {#each channels as channel (channel.id)}
      <li>
        <button
          class="row"
          class:used={usedChannelIds.includes(channel.id)}
          data-focusable
          onclick={() => onPick(channel)}
        >
          <span class="name">{channel.name}</span>
          {#if nowPlaying[channel.id]}
            <span class="now">{nowPlaying[channel.id]!.title}</span>
          {/if}
          <span class="faint">{channel.group ?? '—'}</span>
        </button>
      </li>
    {/each}
    {#if channels.length === 0 && !loading}
      <li class="faint pad">Keine Treffer.</li>
    {/if}
  </ul>
</div>

<style>
  .picker {
    background: var(--bg-raised);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    padding: calc(var(--pad) * 0.6) var(--pad);
    display: flex;
    flex-direction: column;
    gap: calc(var(--gap) * 0.75);
    min-width: 0;
  }
  .head { display: flex; align-items: center; justify-content: space-between; gap: var(--gap); }
  h4 { margin: 0; font-size: var(--font-base); color: var(--text-dim); font-weight: 600; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .filters { display: flex; flex-wrap: wrap; gap: var(--gap); align-items: center; }
  .filters input:not([type]) { flex: 1 1 12em; min-width: 0; }
  .filters select { max-width: 14em; }
  .check { display: flex; align-items: center; gap: 0.4em; font-size: var(--font-sm); }
  .list {
    list-style: none;
    margin: 0;
    padding: 4px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-height: 40vh;
    overflow-y: auto;
  }
  .list.dim { opacity: 0.6; }
  .row {
    width: 100%;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.2em 0.8em;
    text-align: left;
    font-size: var(--font-sm);
  }
  .row.used { border-color: var(--accent); }
  .name { font-weight: 600; font-size: var(--font-base); }
  .now { color: var(--accent); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
  .pad { padding: 0.5em; }
  .error { color: var(--danger, #e66); margin: 0; font-size: var(--font-sm); }
</style>
